<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { computed } from 'vue'
import { PhEnvelopeOpen, PhArrowRight } from '@phosphor-icons/vue'

import {
  Footer,
  FooterContent,
  FooterNewsletter,
  FooterNewsletterHeadline,
  FooterNewsletterContent,
  FooterTitle,
  FooterSocialLinks,
  FacebookIcon,
  InstagramIcon,
  PinterestIcon,
  MagazineCover,
  MagazineContent,
  MagazineDescription,
  MagazinePreview,
  FooterLinkSeparator,
  FooterLinks,
  FooterLinkItem,
  FooterLegal,
  Badge,
  Button
} from '@toni/brand-ui'

import { useConfigStore } from '../../stores/config'
import { PageBody } from '../../utils/page-body-schema'
import { useClient } from '../../utils/client-only'
import { useMediaQuery } from '../../utils/use-media-query'
import { FooterMenu, FooterData } from '../../utils/footer-schema'
import { useCapitalizeFirst } from '../../utils/use-capitalize-first'

const props = defineProps<{
  value: FooterMenu,
  footerData: FooterData,
  pageData: PageBody,
  isHome: boolean
}>()

const menu = props.value.menu ?? []

const { isClient } = useClient()

const { t } = useI18n()

const isSm = useMediaQuery('sm')

const menuNeedsSplit = menu.length > 9

const splitMenu = computed(() => {
  const splitIndex = Math.ceil(menu.length / 2)
  if (!menuNeedsSplit) {
    return {
      firstPart: menu,
      secondPart: []
    }
  }
  return {
    firstPart: menu.slice(0, splitIndex),
    secondPart: menu.slice(splitIndex)
  }
})

const socialLinks = computed(() => useConfigStore().rsConfig?.socialSettings?.socialLinks)

const hasSocialLinks = computed(() => socialLinks.value && Object.keys(socialLinks.value).length > 0)

const socialNetworkComponents = {
  facebook: FacebookIcon,
  instagram: InstagramIcon,
  pinterest: PinterestIcon
}

const capitalizeFirst = useCapitalizeFirst()
</script>

<template>
  <Footer data-testid="footer">
    <FooterNewsletter>
      <Badge variant="quarternary"
             class="absolute -translate-y-1/2 top-0 left-6 text-badge-lg px-2 pt-1 pb-[3px]">
        {{ footerData.badge }}
      </Badge>
      <FooterNewsletterContent class="sm:flex-1">
        <FooterNewsletterHeadline>
          {{ footerData.newsletter?.headline }}
        </FooterNewsletterHeadline>
        <p class="text-ui-sm sm:text-body-md">
          {{ footerData.newsletter?.text }}
        </p>
      </FooterNewsletterContent>
      <Button v-if="isClient"
              variant="primary"
              :inverted="true"
              :size="isSm ? 'xl' : 'lg'"
              href="/newsletter"
              class="flex items-center justify-center gap-[6px] w-full px-8 sm:w-fit">
        <PhEnvelopeOpen weight="bold" />
        {{ footerData?.newsletter?.button }}
      </Button>
    </FooterNewsletter>
    <FooterContent :class="['flex flex-col sm:flex-row sm:mb-4', { 'gap-8': hasSocialLinks }]">
      <FooterTitle>
        <slot />
        <template v-if="isHome">
          <h1>{{ pageData?.header?.headline }}</h1>
        </template>
        <template v-else>
          {{ footerData.content?.title }}
        </template>
      </FooterTitle>
      <FooterSocialLinks>
        <Button v-for="(socialNetworkUrl, socialNetwork) in socialLinks"
                :key="`social-link-${socialNetwork}`"
                variant="primary"
                type="icon-only"
                :inverted="true"
                :href="socialNetworkUrl"
                target="_blank"
                rel="nofollow"
                :aria-label="`${t('footer.socialLinks.ariaLabel')} ${capitalizeFirst(String(socialNetwork))}`">
          <component :is="socialNetworkComponents[String(socialNetwork) as keyof typeof socialNetworkComponents]" />
        </Button>
      </FooterSocialLinks>
    </FooterContent>
    <MagazinePreview>
      <MagazineCover :src="footerData.magazine?.cover ?? ''"
                     :alt="footerData.magazine?.coverAlt ?? ''" />
      <MagazineContent>
        <MagazineDescription class="mb-2">
          {{ footerData.magazine?.description }}
        </MagazineDescription>
        <Button variant="link"
                size="sm"
                :inverted="true"
                href="https://www.bauer-plus.de/abo/wunderweib/intouch"
                rel="nofollow"
                target="_blank">
          {{ footerData.magazine?.button }}
          <PhArrowRight weight="bold"
                        class="h-4 w-4" />
        </Button>
      </MagazineContent>
    </MagazinePreview>
    <FooterContent>
      <FooterLinks class="flex flex-wrap justify-start">
        <FooterLinkSeparator class="my-0" />

        <FooterLinkItem v-for="item in splitMenu.firstPart"
                        :key="item.name">
          <a :href="item.url"
             :target="item.target ? item.target : undefined">
            {{ item.name }}
          </a>
        </FooterLinkItem>

        <FooterLinkSeparator v-if="menuNeedsSplit"
                             class="hidden sm:block w-full my-0" />

        <template v-if="menuNeedsSplit">
          <FooterLinkItem v-for="item in splitMenu.secondPart"
                          :key="item.name">
            <a :href="item.url"
               :target="item.target ? item.target : undefined">
              {{ item.name }}
            </a>
          </FooterLinkItem>
        </template>

        <FooterLinkSeparator class="my-0" />
      </FooterLinks>
    </FooterContent>
    <FooterLegal />
  </Footer>
</template>
